import { WizardFormData, StepErrors, ValidationResult } from './types';
import { EMAIL_REGEX, MAX_EMAIL_LENGTH, MAX_NAME_LENGTH, GUEST_ROLE } from './constants';

/**
 * Reusable name validation helper for firstname/lastname fields.
 * Returns valid if 1 <= trimmed length <= 100.
 */
export function validateName(value: string): ValidationResult {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return { valid: false, errors: { name: 'Name is required' } };
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
        return { valid: false, errors: { name: `Name must not exceed ${MAX_NAME_LENGTH} characters` } };
    }
    return { valid: true };
}

/**
 * Step 0 — Email: validates email format/length and firstname/lastname.
 * Collects all errors simultaneously (collect-all pattern).
 */
export function validateStepEmail(data: WizardFormData): ValidationResult {
    const errors: StepErrors = {};
    const email = data.email.trim();
    const firstname = data.firstname.trim();
    const lastname = data.lastname.trim();

    if (!email) {
        errors.email = 'Email is required';
    } else if (email.length > MAX_EMAIL_LENGTH) {
        errors.email = 'Email must not exceed 254 characters';
    } else if (!EMAIL_REGEX.test(email)) {
        errors.email = 'Invalid email format';
    }

    if (!firstname) {
        errors.firstname = 'First name is required';
    } else if (firstname.length > MAX_NAME_LENGTH) {
        errors.firstname = 'First name must not exceed 100 characters';
    }

    if (!lastname) {
        errors.lastname = 'Last name is required';
    } else if (lastname.length > MAX_NAME_LENGTH) {
        errors.lastname = 'Last name must not exceed 100 characters';
    }

    return Object.keys(errors).length === 0
        ? { valid: true }
        : { valid: false, errors };
}

/**
 * Step 1 — Role: validates that a role has been selected.
 */
export function validateStepRole(data: WizardFormData): ValidationResult {
    if (data.role_id === null) {
        return { valid: false, errors: { role_id: 'Please select a role' } };
    }
    return { valid: true };
}

/**
 * Step 2 — BU & Position: validates both business unit and position are selected.
 */
export function validateStepBUPosition(data: WizardFormData): ValidationResult {
    const errors: StepErrors = {};

    if (data.bu_id === null) {
        errors.bu_id = 'Please select a business unit';
    }
    if (data.position_id === null) {
        errors.position_id = 'Please select a position';
    }

    return Object.keys(errors).length === 0
        ? { valid: true }
        : { valid: false, errors };
}

/**
 * Step 3 — Location: validates bucket selection for non-Guest roles.
 * Guest roles bypass bucket validation entirely.
 */
export function validateStepLocation(data: WizardFormData, roleName: string): ValidationResult {
    // Guest role skips bucket validation
    if (roleName.toLowerCase() === GUEST_ROLE.toLowerCase()) {
        return { valid: true };
    }

    const hasBucketSelection = data.selectedBucketIds.length > 0;
    const hasTextFieldSelection = data.locations.length > 0;

    if (!hasBucketSelection && !hasTextFieldSelection) {
        return {
            valid: false,
            errors: { bucket: 'Please select at least one bucket or location' },
        };
    }

    return { valid: true };
}

/**
 * Step 4 — Timezone: validates that a timezone has been selected.
 */
export function validateStepTimezone(data: WizardFormData): ValidationResult {
    if (!data.timezone.trim()) {
        return { valid: false, errors: { timezone: 'Please select a timezone' } };
    }
    return { valid: true };
}
